var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, DatePickerDealButton, Paper, Stacker, Styler, } from '@honeycomb/ui-core';
import { useGlobalContext, TripType, useSearch, useHotelTeaserPrices, DateSearchType, } from '@honeycomb/data';
import { DateTime } from 'luxon';
import { priceCalendarSearchCreatedRouting } from '@honeycomb/routing';
import { HotelPriceCalendarPriceModeToggle, HotelPriceCalendarSkeleton, } from '../../hotel-price-calendar';
import { getCurrentDate } from '../../../utils/date-utils';
import { calendarSideEffect, getDealDates, getDestinations, ISODATEFORMAT, } from '../../hotel-price-calendar/calendarUtil';
import { LandingPagePriceCalendarHeader } from './LandingPagePriceCalendarHeader/LandingPagePriceCalendarHeader';
import { useHotelDetailsContext } from '../HotelDetails/HotelDetails.context';
import { PRICE_CALENDAR_COLUMN_SPACING, PRICE_CALENDAR_ROW_SPACING } from '../../../utils';
import { HotelDetailsActionType } from '../HotelDetails';
export function LandingPagePriceCalendar(_a) {
    var _b, _c, _d, _e, _f;
    var { hotel } = _a, other = __rest(_a, ["hotel"]);
    const { resourceStrings: { HC_DEAL }, configSettings: { HCDefaultCalendarConfig, HCDestinationCalendarConfig, PricingCalendarVisibilityThreshold }, } = useGlobalContext();
    const detailsContext = useHotelDetailsContext();
    const { estabId, geography } = hotel;
    const searchResults = useSearch([priceCalendarSearchCreatedRouting(estabId)]);
    const [searchState, setSearch] = searchResults;
    const { createSearch } = setSearch;
    const { tripType: trip } = searchState;
    const [waitingForSearch, setWaitingForSearch] = React.useState(false);
    const [hotelTeaserPricesRequest, setHotelTeaserPricesRequest] = useState({
        estabID: Number(estabId),
    });
    const teaserPricesResults = useHotelTeaserPrices(hotelTeaserPricesRequest, [
        (data) => {
            calendarSideEffect(data, detailsContext, parseInt(PricingCalendarVisibilityThreshold));
        },
    ], [
        (_error) => {
            detailsContext.dispatch({ type: HotelDetailsActionType.SET_PRICING_CALENDAR_TOGGLE, payload: false });
            detailsContext.dispatch({
                type: HotelDetailsActionType.SET_PRICING_CALENDAR_INITIAL_LOAD,
                payload: true,
            });
        },
    ], !(trip === TripType.Holiday || trip === TripType.Hotel));
    const destinations = useMemo(() => {
        return getDestinations(hotel, searchState.destinations);
    }, [hotel, searchState.destinations]);
    // When clicking on specific date in the calendar.
    const handleSelectDate = React.useCallback((duration, tripType, departureAirports) => (day) => {
        const updatedStartDate = DateTime.fromJSDate(day).toFormat(ISODATEFORMAT);
        const updatedEndDate = DateTime.fromMillis(day.getTime() + duration * 24 * 60 * 60 * 1000).toFormat(ISODATEFORMAT);
        if (!updatedStartDate || !updatedEndDate) {
            return;
        }
        setWaitingForSearch(true);
        const updatedSearchState = Object.assign(Object.assign(Object.assign(Object.assign({}, searchState), { startDate: updatedStartDate, endDate: updatedEndDate, destinations, dateSearchType: DateSearchType.ChooseDate, duration: null, flexibleDays: null }), ((departureAirports === null || departureAirports === void 0 ? void 0 : departureAirports.length) && { departureAirports, tripType: TripType.Holiday })), (tripType && { tripType }));
        createSearch(updatedSearchState);
    }, [createSearch, destinations, searchState]);
    const defaultCalendarConfig = JSON.parse(HCDefaultCalendarConfig);
    const DestinationConfig = JSON.parse(HCDestinationCalendarConfig);
    const { people, duration: defaultDuration, departureAirports, tripType, dealReferenceDate } = defaultCalendarConfig;
    const duration = (_c = DestinationConfig[(_b = geography === null || geography === void 0 ? void 0 : geography.province) !== null && _b !== void 0 ? _b : '']) !== null && _c !== void 0 ? _c : defaultDuration;
    const [selectedTrip, setSelectedTrip] = useState(tripType);
    const [displayMonthHotel, setDisplayMonthHotel] = useState();
    const [displayMonthHoliday, setDisplayMonthHoliday] = useState();
    // TODO, one for the future price mode for the estab landing page should come from global toggle value,
    const [priceMode, setPriceMode] = useState('total');
    const { data: hotelTeaserPrices, loading: waitingForPrices } = teaserPricesResults;
    const isHoliday = selectedTrip === TripType.Holiday;
    const displayedMonth = isHoliday ? displayMonthHoliday : displayMonthHotel;
    useEffect(() => {
        const teaserPricesRequest = {
            estabID: Number(estabId),
            isHoliday,
            people,
            duration,
            departureAirports,
        };
        setHotelTeaserPricesRequest(teaserPricesRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // On changing tabs of landing page price calendar
    const handleTabChange = (val) => {
        const isHolidayTab = val === TripType.Holiday;
        const selectedMonth = isHolidayTab ? displayMonthHoliday : displayMonthHotel;
        const dateTime = selectedMonth ? DateTime.fromISO(selectedMonth.toISOString()) : undefined;
        const calendarMonthYear = dateTime ? `${dateTime.month}-${dateTime.year}` : undefined;
        setSelectedTrip(val);
        setHotelTeaserPricesRequest((prevrequest) => (Object.assign(Object.assign({}, prevrequest), { isHoliday: isHolidayTab, calendarMonthYear })));
    };
    const currentDate = new Date(getCurrentDate().toISO()).setHours(0, 0, 0, 0);
    useEffect(() => {
        var _a, _b;
        if (!displayedMonth && ((_b = (_a = hotelTeaserPrices === null || hotelTeaserPrices === void 0 ? void 0 : hotelTeaserPrices.hotelTeaserPrices) === null || _a === void 0 ? void 0 : _a.prices) === null || _b === void 0 ? void 0 : _b[0])) {
            const priceStartDate = new Date(hotelTeaserPrices.hotelTeaserPrices.prices[0].date);
            if (isHoliday)
                setDisplayMonthHoliday(priceStartDate);
            else
                setDisplayMonthHotel(priceStartDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(_d = hotelTeaserPrices === null || hotelTeaserPrices === void 0 ? void 0 : hotelTeaserPrices.hotelTeaserPrices) === null || _d === void 0 ? void 0 : _d.prices, displayedMonth]);
    const calendarPrices = (_f = (_e = hotelTeaserPrices === null || hotelTeaserPrices === void 0 ? void 0 : hotelTeaserPrices.hotelTeaserPrices) === null || _e === void 0 ? void 0 : _e.prices) === null || _f === void 0 ? void 0 : _f.filter((dateValue) => {
        var _a;
        return ((_a = displayedMonth === null || displayedMonth === void 0 ? void 0 : displayedMonth.getMonth()) !== null && _a !== void 0 ? _a : 0) + 1 === DateTime.fromISO(dateValue.date).month;
    });
    const minDealPriceArray = useMemo(() => {
        var _a;
        if (!displayedMonth)
            return undefined;
        const startDate = new Date((_a = new Date(displayedMonth)) === null || _a === void 0 ? void 0 : _a.setDate(dealReferenceDate));
        return getDealDates(startDate, calendarPrices);
    }, [calendarPrices, dealReferenceDate, displayedMonth]);
    const showPriceToggle = calendarPrices === null || calendarPrices === void 0 ? void 0 : calendarPrices.some((priceData) => priceData === null || priceData === void 0 ? void 0 : priceData.price);
    const customDayButton = (_a) => {
        var _b;
        var { day, modifiers } = _a, buttonProps = __rest(_a, ["day", "modifiers"]);
        const item = calendarPrices === null || calendarPrices === void 0 ? void 0 : calendarPrices.find((e) => new Date(e.date).toDateString() === day.date.toDateString());
        const monthDate = new Date(item === null || item === void 0 ? void 0 : item.date).setHours(0, 0, 0, 0);
        const minDeal = minDealPriceArray === null || minDealPriceArray === void 0 ? void 0 : minDealPriceArray.find((e) => e === (item === null || item === void 0 ? void 0 : item.date));
        return (React.createElement(DatePickerDealButton, Object.assign({ calloutLabel: monthDate >= currentDate && minDeal ? HC_DEAL : '', price: (monthDate >= currentDate &&
                ((_b = item === null || item === void 0 ? void 0 : item.price) !== null && _b !== void 0 ? _b : 0) > 0 &&
                (priceMode === 'perPerson' ? item === null || item === void 0 ? void 0 : item.pricePerPersonFormatted : item === null || item === void 0 ? void 0 : item.priceFormatted)) ||
                '', day: day, modifiers: modifiers }, buttonProps)));
    };
    const handleMonthChange = (date) => {
        const calendarDate = DateTime.fromISO(date.toISOString());
        if (isHoliday)
            setDisplayMonthHoliday(date);
        else
            setDisplayMonthHotel(date);
        setHotelTeaserPricesRequest((prevrequest) => (Object.assign(Object.assign({}, prevrequest), { calendarMonthYear: `${calendarDate.month}-${calendarDate.year}` })));
    };
    const loading = waitingForPrices || waitingForSearch;
    if (!loading && !(calendarPrices === null || calendarPrices === void 0 ? void 0 : calendarPrices.length)) {
        return null;
    }
    return (React.createElement(Paper, Object.assign({ py: 5, defaultGutters: true }, other),
        React.createElement(Stacker, { spacing: 0, "data-id": "calendar_with_price" },
            React.createElement(LandingPagePriceCalendarHeader, { duration: duration, selectedTrip: selectedTrip, handleTabChange: handleTabChange }),
            React.createElement(Styler, { display: "flex", flexDirection: "column" },
                loading && React.createElement(HotelPriceCalendarSkeleton, { w: "100%" }),
                !loading && displayedMonth && (React.createElement(DatePicker, { onSelectDate: handleSelectDate(duration !== null && duration !== void 0 ? duration : 7, selectedTrip, isHoliday ? departureAirports !== null && departureAirports !== void 0 ? departureAirports : [] : undefined), month: displayedMonth, customDayButton: customDayButton, onMonthChange: handleMonthChange, fullWidth: true, dayRowSpacing: PRICE_CALENDAR_ROW_SPACING, dayColumnSpacing: PRICE_CALENDAR_COLUMN_SPACING }))),
            showPriceToggle && (React.createElement(Styler, { display: "flex", justifyContent: "center" },
                React.createElement(HotelPriceCalendarPriceModeToggle, { value: priceMode, onChange: (value) => setPriceMode(value) }))))));
}
